/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress, Card, Stack, Box, IconButton } from '@mui/material';
import type { FeatureCollection, Geometry } from 'geojson';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AlertBanner, dateUtils, referenceTimeUtils } from '@opengeoweb/shared';
import type { Coordinates } from '@opengeoweb/shared';
import type { CoreAppStore, syncGroupsTypes } from '@opengeoweb/store';
import {
  genericSelectors,
  layerActions,
  mapSelectors,
  mapActions,
  useUpdateFeatureSyncLayers,
  useRegisterFeatureSyncSource,
  useSyncgroupTimeState,
  genericActions,
  uiActions,
  uiTypes,
  syncGroupsSelectors,
} from '@opengeoweb/store';
import { Close } from '@opengeoweb/theme';
import { handleDateUtilsISOString, webmapUtils } from '@opengeoweb/webmap';
import { MapFeatureClass } from '@opengeoweb/webmap-react';
import { SOUNDING_COLORS } from './drawUtils';
import { SharedMouseYProvider } from './SharedMouseYProvider';
import { SoundingsLegend } from './SoundingsLegend';
import { getTimeMismatchInfo } from './timeMismatchUtils';
import {
  useGetLocations,
  useGridPointsFromRadius,
  useMultipleSources,
  useCheckPositionQuerySupportBySource,
  useCheckRadiusQuerySupportBySource,
  useResolvedSourceSelections,
  useTimesteps,
} from '../../api/edr-api/hooks';
import type {
  FeaturesFromBE,
  SoundingDiagramType,
} from '../../api/edr-api/types';
import { soundingsActions } from '../../store/reducer';
import {
  getDiagramType,
  getSelectedTime,
  getSelectedSources,
  getLocationSelections,
  getSoundingPanelsInOrder,
  getShouldHideLocationMarkers,
  getSoundingsPreset,
  getProfileDataPreferences,
  getIsLegendVisible,
  getSoundingsCollections,
} from '../../store/selectors';
import type {
  SoundingsViewState,
  SoundingsModuleState,
} from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';
import { useSoundingsTranslation } from '../../utils/i18n';
import { MenuBarConnect } from '../MenuBar/MenuBarConnect';
import { SoundingsPanelConnect } from '../SoundingsPanels/SoundingsPanelConnect';
import { SoundingsPanels } from '../SoundingsPanels/SoundingsPanels';
import { SoundingsTimeSliderConnect } from '../SoundingsTimeSlider';
import { findNearestLocationFeature } from '../utils';
import { SOUNDINGS_CONTAINER_NAME } from './constants';

interface SoundingsConnectProps {
  viewId: string;
  soundingsPreset?: SoundingsViewState;
}

export const SoundingsConnect = ({
  viewId,
  soundingsPreset,
}: SoundingsConnectProps): React.ReactElement => {
  const soundingsRef = useRef<HTMLDivElement>(null);

  const { t } = useSoundingsTranslation();

  const geojsonLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const dispatch = useDispatch();

  // Track highlighted features per source (sourceId -> featureId)
  // Each source that doesn't support position queries will have its nearest feature highlighted
  const [highlightedFeaturesBySource, setHighlightedFeaturesBySource] =
    useState<Record<string, string>>({});

  // Ref to track highlighted features synchronously (state updates are async)
  const highlightedFeaturesBySourceRef = React.useRef<Record<string, string>>(
    {},
  );

  // Wrapper to update both state and ref together
  const updateHighlightedFeaturesBySource = React.useCallback(
    (updates: Record<string, string>) => {
      highlightedFeaturesBySourceRef.current = updates;
      setHighlightedFeaturesBySource(updates);
    },
    [],
  );

  const soundingsPresetRef = React.useRef<SoundingsViewState | undefined>(
    undefined,
  );
  const presetJustLoadedRef = React.useRef(false);
  // Flag to prevent re-entrant update cycles between location sync effects
  const isProcessingLocationUpdateRef = React.useRef(false);
  // Track the last processed targetLocation to allow re-processing when it changes
  const lastProcessedTargetLocationRef = React.useRef<
    [number, number] | undefined
  >(undefined);
  const initialLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});
  const previousLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});

  const currentSoundingsPreset = useSelector((state: SoundingsModuleState) =>
    getSoundingsPreset(state, viewId),
  );

  const isTimeScrollingEnabled = useSelector(
    syncGroupsSelectors.isTimeScrollingEnabled,
  );

  React.useLayoutEffect(() => {
    dispatch(soundingsActions.registerSoundingsView({ viewId }));
    if (
      soundingsPreset &&
      !isEqual(soundingsPreset, soundingsPresetRef.current)
    ) {
      soundingsPresetRef.current = soundingsPreset;
      // Skip re-registering if the incoming preset already matches the current
      // soundings store state (e.g. workspace initialProps updated after a save)
      if (!isEqual(soundingsPreset, currentSoundingsPreset)) {
        presetJustLoadedRef.current = true;
        initialLocationsRef.current = soundingsPreset.selectedLocations || {};
        previousLocationsRef.current = {};
        dispatch(
          soundingsActions.registerSoundingsPreset({
            viewId,
            preset: soundingsPreset,
          }),
        );
        dispatch(
          uiActions.setActiveMapIdForDialog({
            type: uiTypes.DialogTypes.SoundingsLocations,
            mapId: viewId,
            setOpen: soundingsPreset.isLocationsDialogOpen ?? false,
          }),
        );
        setTimeout(() => {
          presetJustLoadedRef.current = false;
          initialLocationsRef.current = {};
        }, 500);
      }
    } else if (!soundingsPreset) {
      soundingsPresetRef.current = undefined;
      initialLocationsRef.current = {};
      previousLocationsRef.current = {};
    }
  }, [currentSoundingsPreset, dispatch, soundingsPreset, viewId]);

  const syncgroupTimeState = useSyncgroupTimeState(viewId, 0);
  const syncgroupSelectedTime = useMemo(() => {
    if (
      syncgroupTimeState?.current === undefined ||
      !Number.isFinite(syncgroupTimeState.current)
    ) {
      return undefined;
    }
    return dateUtils.fromUnix(syncgroupTimeState.current).toISOString();
  }, [syncgroupTimeState]);
  const isTimeSynced = !!syncgroupSelectedTime;

  React.useEffect(() => {
    if (soundingsPreset?.selectedTime && isTimeSynced) {
      // trigger sync of time to map when preset has selectedTime
      dispatch(
        genericActions.setTime({
          sourceId: viewId,
          origin: 'SoundingsConnect',
          value: {
            currentTime: handleDateUtilsISOString(soundingsPreset.selectedTime),
          },
        }),
      );
    }
  }, [dispatch, isTimeSynced, soundingsPreset?.selectedTime, viewId]);

  useRegisterFeatureSyncSource(viewId);

  const selectedTime = useSelector((state: SoundingsModuleState) =>
    getSelectedTime(state, viewId),
  );
  const rawSourceSelections = useSelector(
    (state: SoundingsModuleState) => getSelectedSources(state, viewId),
    shallowEqual,
  );
  // Stabilize sourceSelections to prevent infinite loops caused by new array references
  const sourceSelectionsRef =
    React.useRef<typeof rawSourceSelections>(undefined);
  const sourceSelections = useMemo(() => {
    if (isEqual(rawSourceSelections, sourceSelectionsRef.current)) {
      return sourceSelectionsRef.current!;
    }
    sourceSelectionsRef.current = rawSourceSelections;
    return rawSourceSelections;
  }, [rawSourceSelections]);

  const resolvedSourceSelections =
    useResolvedSourceSelections(sourceSelections);

  const locationSelections = useSelector(
    (state: SoundingsModuleState) => getLocationSelections(state, viewId),
    shallowEqual,
  );
  const soundingsCollections = useSelector((state: SoundingsModuleState) =>
    getSoundingsCollections(state),
  );
  const diagramType = useSelector((state: SoundingsModuleState) =>
    getDiagramType(state, viewId),
  );
  const shouldHideLocationMarkers = useSelector((state: SoundingsModuleState) =>
    getShouldHideLocationMarkers(state, viewId),
  );
  const profileDataPreferences = useSelector((state: SoundingsModuleState) =>
    getProfileDataPreferences(state, viewId),
  );
  const isLegendVisible = useSelector((state: SoundingsModuleState) =>
    getIsLegendVisible(state, viewId),
  );
  const selectedMapIds = useSelector(
    (state: CoreAppStore) => genericSelectors.getLinkedTargetIds(state, viewId),
    shallowEqual,
  );
  const ownedFeatureLayerIds = useSelector(
    (state: CoreAppStore) =>
      genericSelectors.selectFeatureSyncLayerIdsForPanel(state, viewId),
    shallowEqual,
  );
  const linkedPanelIdsByMapId = useSelector(
    (state: CoreAppStore) =>
      Object.fromEntries(
        selectedMapIds.map((mapId) => [
          mapId,
          genericSelectors.selectLinkedPanelIdsForMap(state, mapId),
        ]),
      ) as Record<string, string[]>,
    shallowEqual,
  );
  const firstLinkedMapId = selectedMapIds[0];
  const { data: sourceTimesteps } = useTimesteps(resolvedSourceSelections);

  const selectedTimeInUse = syncgroupSelectedTime || selectedTime;
  const timeMismatchInfo = useMemo(() => {
    return getTimeMismatchInfo(
      isTimeSynced,
      selectedTimeInUse,
      sourceTimesteps,
    );
  }, [isTimeSynced, selectedTimeInUse, sourceTimesteps]);
  const mismatchKey = timeMismatchInfo ? selectedTimeInUse || '' : '';
  const [isTimeMismatchDismissed, setIsTimeMismatchDismissed] =
    React.useState(false);

  const setChosenDiagramType = React.useCallback(
    (diagramType: SoundingDiagramType) => {
      dispatch(
        soundingsActions.updateDiagramType({
          diagramType,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const selectedFeature = useSelector(
    (state: CoreAppStore) => genericSelectors.getSelectedFeature(state, viewId),
    shallowEqual,
  );
  // Ref for reading selectedFeature in effects without causing dependency loops
  const selectedFeatureRef = React.useRef(selectedFeature);
  selectedFeatureRef.current = selectedFeature;

  const selectedMapPin = useSelector(
    (state: CoreAppStore) =>
      // mapPins are synced so we can arbitrarily choose the first linked map
      mapSelectors.getPinLocation(state, firstLinkedMapId),
    shallowEqual,
  );
  // Ref for reading selectedMapPin in effects without causing dependency loops
  const selectedMapPinRef = React.useRef(selectedMapPin);
  selectedMapPinRef.current = selectedMapPin;

  interface CollectionItem {
    id: string;
    url: string;
    name: string;
    instanceId?: string;
  }
  const collectionsRef = React.useRef<CollectionItem[]>(undefined);
  const collections = useMemo(() => {
    // Only use instanceId for location fetching when the preset configures it, so collection-level sources (e.g. harmonie) are not affected
    const presetMap = new Map(
      soundingsCollections.map((collection) => [collection.id, collection]),
    );
    const newCollections = sourceSelections.map(
      ({ collectionId, url, name, instanceId }) => ({
        id: collectionId,
        url,
        name: name || collectionId,
        instanceId:
          presetMap.get(collectionId)?.instanceId !== undefined
            ? instanceId
            : undefined,
      }),
    );
    if (isEqual(newCollections, collectionsRef.current)) {
      return collectionsRef.current!;
    }
    collectionsRef.current = newCollections;
    return newCollections;
  }, [sourceSelections, soundingsCollections]);

  const { data: rawLocationData } = useGetLocations(collections, true);

  const { data: positionQuerySupportBySource } =
    useCheckPositionQuerySupportBySource(resolvedSourceSelections);
  const { data: radiusQuerySupportBySource } =
    useCheckRadiusQuerySupportBySource(resolvedSourceSelections);

  // Stabilize locationData to prevent infinite loops caused by new array references
  const locationDataRef = React.useRef<typeof rawLocationData>(undefined);
  const locationData = useMemo(() => {
    if (isEqual(rawLocationData, locationDataRef.current)) {
      return locationDataRef.current;
    }
    locationDataRef.current = rawLocationData;
    return rawLocationData;
  }, [rawLocationData]);

  // Resolved [lon, lat] per source: string selection → snapped grid feature,
  // array selection → free-position coordinate.
  const sourceGridPoints = useMemo<
    Record<string, [number, number] | undefined>
  >(() => {
    const result: Record<string, [number, number] | undefined> = {};
    sourceSelections.forEach((source) => {
      const selection = locationSelections?.[source.collectionId];
      if (typeof selection === 'string') {
        const sourceLocations = locationData?.find(
          (service) => service.id === source.collectionId,
        );
        const feature = sourceLocations?.features?.find(
          (feature) => feature.id === selection,
        );
        if (feature?.geometry.type === 'Point') {
          result[source.collectionId] = feature.geometry.coordinates as [
            number,
            number,
          ];
        }
      } else if (Array.isArray(selection)) {
        result[source.collectionId] = selection;
      }
    });
    return result;
  }, [sourceSelections, locationSelections, locationData]);

  const sourceLocationNames = useMemo<
    Record<string, string | undefined>
  >(() => {
    const result: Record<string, string | undefined> = {};
    sourceSelections.forEach((source) => {
      const selection = locationSelections?.[source.collectionId];
      if (typeof selection !== 'string') {
        return;
      }
      const sourceLocations = locationData?.find(
        (service) => service.id === source.collectionId,
      );
      const feature = sourceLocations?.features?.find(
        (feature) => feature.id === selection,
      );
      const name = feature?.properties.name;
      if (name) {
        result[source.collectionId] = name;
      }
    });
    return result;
  }, [sourceSelections, locationSelections, locationData]);

  /** Name of a directly picked location (map marker or Locations menu). Stays
   * undefined for free position clicks, where a location can only be resolved
   * as a nearby fallback for sources without position query support.
   */
  const selectedLocationName = useMemo<string | undefined>(() => {
    const directlySelectedSource = sourceSelections.find((source) => {
      const selection = locationSelections?.[source.collectionId];
      return (
        typeof selection === 'string' &&
        highlightedFeaturesBySource[source.collectionId] !== selection
      );
    });
    return directlySelectedSource
      ? sourceLocationNames[directlySelectedSource.collectionId]
      : undefined;
  }, [
    sourceSelections,
    locationSelections,
    highlightedFeaturesBySource,
    sourceLocationNames,
  ]);

  const sourceResolvedInstanceIds = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    resolvedSourceSelections.forEach((source) => {
      const hasConcreteReferenceTime =
        !!source.instanceId &&
        !referenceTimeUtils.isRelativeReferenceTime(source.instanceId);
      if (hasConcreteReferenceTime) {
        result[source.collectionId] = source.instanceId!;
      }
    });
    return result;
  }, [resolvedSourceSelections]);

  /* Make sure the map pin is visible when linking new maps */
  useEffect(() => {
    const toggleMapPinVisibility = (displayMapPin: boolean): void => {
      selectedMapIds.forEach((mapId) => {
        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin,
          }),
        );
      });
    };

    toggleMapPinVisibility(true);

    return (): void => {
      selectedMapIds.forEach((mapId) => {
        const linkedPanelIds = linkedPanelIdsByMapId[mapId] ?? [];
        const isStillLinkedToAnotherPanel = linkedPanelIds.some(
          (panelId) => panelId !== viewId,
        );

        if (isStillLinkedToAnotherPanel) {
          return;
        }

        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin: false,
          }),
        );
      });
    };
  }, [dispatch, linkedPanelIdsByMapId, selectedMapIds, viewId]);

  // construct and send geojson to map for drawing locations
  const geoJsonForMapRef =
    React.useRef<
      FeatureCollection<Geometry, syncGroupsTypes.PotentialDataProperties>
    >(undefined);
  const geoJsonForMap = useMemo<
    FeatureCollection<Geometry, syncGroupsTypes.PotentialDataProperties>
  >(() => {
    const locationFeatures = (locationData || []).flatMap(
      ({ id: serviceId, features }) =>
        (
          (features as FeaturesFromBE[] | undefined) ?? ([] as FeaturesFromBE[])
        ).map((feature) => {
          // Check if this feature is highlighted by its source (for sources without position support)
          const isHighlightedBySource =
            highlightedFeaturesBySource[serviceId] === feature.id;

          // Get the source index to determine color (same color as sounding diagram)
          const sourceIndex = sourceSelections.findIndex(
            (source) => source.collectionId === serviceId,
          );
          const highlightColor = isHighlightedBySource
            ? SOUNDING_COLORS[sourceIndex % SOUNDING_COLORS.length]
            : undefined;

          // Check if any source is highlighting this feature (for legacy highlightNearbyFeature)
          const isHighlightedByAnySource = Object.values(
            highlightedFeaturesBySource,
          ).includes(feature.id as string);

          return {
            type: 'Feature' as const,
            geometry: feature.geometry,
            id: feature.id,
            properties: {
              name: feature.properties.name,
              mapFeatureClass: MapFeatureClass.ObservationStation,
              // Per-source highlight color for nearest feature (same color as sounding diagram)
              highlightColor,
              // Legacy: boolean for backward compatibility with map styling
              highlightNearbyFeature: isHighlightedBySource,
              // Red circle only for directly selected feature (not via nearby click)
              highlightSelectedFeature:
                selectedFeature?.id === feature.id && !isHighlightedByAnySource,
              id: feature.id,
              serviceId,
              hidden: shouldHideLocationMarkers,
            },
          };
        }),
    );

    const newGeoJson = {
      type: 'FeatureCollection' as const,
      features: locationFeatures,
    };

    // Stabilize the reference to prevent unnecessary effect triggers
    if (isEqual(newGeoJson, geoJsonForMapRef.current)) {
      return geoJsonForMapRef.current!;
    }
    geoJsonForMapRef.current = newGeoJson;
    return newGeoJson;
  }, [
    locationData,
    shouldHideLocationMarkers,
    highlightedFeaturesBySource,
    selectedFeature?.id,
    sourceSelections,
  ]);

  // Map location selection has changed. Update soundings location
  // since the location menu may be out of sync

  // Use ref to avoid circular dependency when reading locationSelections
  // Update ref immediately (not in effect) to ensure it's always current
  const locationSelectionsRef = React.useRef(locationSelections);
  locationSelectionsRef.current = locationSelections;

  // Stabilize targetLocation to prevent infinite loops caused by new array references
  const targetLocationRef = React.useRef<[number, number] | undefined>(
    undefined,
  );

  // Compute target location from map pin or selected feature
  const targetLocation = useMemo<[number, number] | undefined>(() => {
    let newLocation: [number, number] | undefined;
    const hasSelectedMapPinLocation =
      typeof selectedMapPin?.lon === 'number' &&
      typeof selectedMapPin.lat === 'number';
    const hasSelectedFeatureLocation =
      typeof selectedFeature?.lon === 'number' &&
      typeof selectedFeature.lat === 'number';

    // Prioritize selectedMapPin for target location if it has valid coordinates,
    // otherwise fall back to selectedFeature
    if (hasSelectedMapPinLocation) {
      newLocation = [selectedMapPin!.lon, selectedMapPin!.lat];
    } else if (hasSelectedFeatureLocation) {
      newLocation = [selectedFeature!.lon, selectedFeature!.lat];
    }

    // Stabilize targetLocation reference
    if (isEqual(newLocation, targetLocationRef.current)) {
      newLocation = targetLocationRef.current;
    } else {
      targetLocationRef.current = newLocation;
    }

    return newLocation;
  }, [selectedFeature, selectedMapPin]);

  // Detect if user clicked on a free map position (not on a location marker)
  // Used to enable grid point marker fetching via radius queries
  const isFreePositionClick = useMemo(() => {
    if (!targetLocation) {
      return false;
    }
    // A free position click is when:
    // 1. selectedFeature doesn't have an id (not a named location marker), OR
    // 2. selectedMapPin is a grid point marker (starts with 'grid-point-')
    const isGridPointMarkerClick =
      typeof selectedMapPin?.id === 'string' &&
      selectedMapPin.id.startsWith('grid-point-');
    const isMapPinForSelectedFeature =
      !!selectedMapPin?.id &&
      selectedMapPin.id === selectedFeature?.id &&
      selectedMapPin.lon === selectedFeature?.lon &&
      selectedMapPin.lat === selectedFeature?.lat;
    return (
      !selectedFeature?.id ||
      isGridPointMarkerClick ||
      !isMapPinForSelectedFeature
    );
  }, [
    targetLocation,
    selectedMapPin?.id,
    selectedMapPin?.lon,
    selectedMapPin?.lat,
    selectedFeature?.id,
    selectedFeature?.lon,
    selectedFeature?.lat,
  ]);

  // Fetch grid points within radius for each source when user clicks on free position
  const { data: gridPointData } = useGridPointsFromRadius(
    resolvedSourceSelections,
    radiusQuerySupportBySource,
    targetLocation,
    selectedTimeInUse,
    isFreePositionClick,
  );

  // Create GeoJSON features for grid point markers (shown when clicking on free map position)
  // Uses server-side radius queries to get accurate grid point positions
  const gridPointFeatures = useMemo(() => {
    if (!isFreePositionClick || !targetLocation) {
      return [];
    }

    // Collect all grid points with their source info
    const gridPoints = sourceSelections
      .map((source, sourceIndex) => {
        const data = gridPointData?.[source.collectionId];
        if (!data?.gridPoint) {
          return null;
        }
        return { source, sourceIndex, gridPoint: data.gridPoint };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    if (gridPoints.length === 0) {
      return [];
    }

    // Group grid points by position to detect overlapping markers between sources
    // Use rounded coordinates as key (6 decimal places ≈ 0.1m precision)
    const positionGroups = new Map<string, typeof gridPoints>();
    gridPoints.forEach((item) => {
      const key = `${item.gridPoint[0].toFixed(6)},${item.gridPoint[1].toFixed(6)}`;
      const group = positionGroups.get(key) || [];
      group.push(item);
      positionGroups.set(key, group);
    });

    // Small fixed offset for markers sharing same grid position (~200m)
    const SOURCE_OVERLAP_OFFSET_DEG = 0.002;

    // Place markers at their true geographic positions.
    // The only adjustment is a small fixed offset when multiple sources share the same grid point.
    return gridPoints.map((item) => {
      const { source, sourceIndex, gridPoint } = item;
      const [baseLon, baseLat] = gridPoint;

      const key = `${baseLon.toFixed(6)},${baseLat.toFixed(6)}`;
      const group = positionGroups.get(key) || [];

      let lon = baseLon;
      let lat = baseLat;

      if (group.length > 1) {
        // Multiple sources share this grid point - offset in circle around it
        const indexInGroup = group.findIndex(
          (gr) => gr.sourceIndex === sourceIndex,
        );
        const offsetAngle = (indexInGroup * 2 * Math.PI) / group.length;
        lon = baseLon + SOURCE_OVERLAP_OFFSET_DEG * Math.cos(offsetAngle);
        lat = baseLat + SOURCE_OVERLAP_OFFSET_DEG * Math.sin(offsetAngle);
      }

      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [lon, lat] },
        id: `grid-point-${source.collectionId}`,
        properties: {
          name: `Grid point (${source.name || source.collectionId})`,
          mapFeatureClass: MapFeatureClass.ObservationStation,
          fill: SOUNDING_COLORS[sourceIndex % SOUNDING_COLORS.length],
          isGridPointMarker: true,
          hidden: shouldHideLocationMarkers,
        },
      };
    });
  }, [
    isFreePositionClick,
    targetLocation,
    gridPointData,
    sourceSelections,
    shouldHideLocationMarkers,
  ]);

  // Combine location features with grid point features for dispatch to map
  const combinedGeoJson = useMemo(() => {
    if (gridPointFeatures.length === 0) {
      return geoJsonForMap;
    }
    return {
      ...geoJsonForMap,
      features: [...geoJsonForMap.features, ...gridPointFeatures],
    };
  }, [geoJsonForMap, gridPointFeatures]);

  const featureSyncLayersById = useMemo(
    () => ({
      [geojsonLayerId]: {
        id: geojsonLayerId,
        geojson: combinedGeoJson,
      },
    }),
    [combinedGeoJson, geojsonLayerId],
  );

  useUpdateFeatureSyncLayers(featureSyncLayersById, viewId);

  useEffect(() => {
    if (presetJustLoadedRef.current) {
      return;
    }

    // Prevent re-entrant updates from location sync effects, but only if
    // we're still processing the same targetLocation. If targetLocation has
    // changed, we need to re-process even if the previous processing hasn't
    // fully completed.
    if (
      isProcessingLocationUpdateRef.current &&
      isEqual(targetLocation, lastProcessedTargetLocationRef.current)
    ) {
      return;
    }

    // Clear highlights if no target location
    if (!targetLocation) {
      updateHighlightedFeaturesBySource({});
      return;
    }

    isProcessingLocationUpdateRef.current = true;
    lastProcessedTargetLocationRef.current = targetLocation;

    // Update location for all selected sources
    const currentSelectedFeature = selectedFeatureRef.current;
    const currentMapPin = selectedMapPinRef.current;

    // First pass: find nearest features for each source and collect for highlight
    // We need to find the overall nearest feature across all sources
    const allNearestFeatures: {
      feature: FeaturesFromBE;
      sourceId: string;
    }[] = [];

    sourceSelections.forEach((source) => {
      if (source.collectionId && targetLocation) {
        // Get features owned by this source
        const sourceData = locationData?.find(
          (service) => service.id === source.collectionId,
        );
        const ownedFeatures = sourceData?.features ?? [];

        // Find the nearest location within search radius that is owned by this source
        const nearestOwnedFeature = findNearestLocationFeature(
          ownedFeatures,
          targetLocation!,
        );

        if (nearestOwnedFeature) {
          allNearestFeatures.push({
            feature: nearestOwnedFeature,
            sourceId: source.collectionId,
          });
        }

        // Check if this source supports EDR position queries
        const supportsPosition =
          positionQuerySupportBySource[source.collectionId] ?? false;
        const currentLocation =
          locationSelectionsRef.current[source.collectionId];

        // Detect if user clicked directly on a location marker owned by this source
        // A direct click means selectedFeature has an id that matches a feature owned by this source
        const isDirectClickOnOwnedMarker =
          !!currentSelectedFeature?.id &&
          nearestOwnedFeature?.id === currentSelectedFeature?.id;

        // When clicking a free position, snap to the nearest grid point from the radius query
        // so the sounding data comes from an actual model grid point rather than an arbitrary coordinate.
        const supportsRadius =
          radiusQuerySupportBySource[source.collectionId] ?? false;
        const isGridPointMarkerClick =
          typeof currentMapPin?.id === 'string' &&
          currentMapPin.id.startsWith('grid-point-');
        const isMapPinForSelectedFeature =
          !!currentMapPin?.id &&
          currentMapPin.id === currentSelectedFeature?.id &&
          currentMapPin.lon === currentSelectedFeature?.lon &&
          currentMapPin.lat === currentSelectedFeature?.lat;
        const isMapPinForCurrentLocation =
          typeof currentLocation === 'string' &&
          currentMapPin?.id === currentLocation;
        const isLocalFreePositionClick =
          !isMapPinForCurrentLocation &&
          (!currentSelectedFeature?.id ||
            isGridPointMarkerClick ||
            !isMapPinForSelectedFeature);
        const gridPoint =
          isLocalFreePositionClick && supportsRadius
            ? gridPointData?.[source.collectionId]?.gridPoint
            : undefined;
        const radiusPending =
          isLocalFreePositionClick &&
          supportsRadius &&
          !gridPointData?.[source.collectionId];

        // Determine the location to use:
        // 1. If user clicked directly on a marker owned by this source: use that marker's ID (location query)
        // 2. If position query is supported AND not a direct click: use nearest grid point coordinates
        //    if available (from radius query), otherwise fall back to exact clicked coordinates
        // 3. If position query is NOT supported: fall back to nearest location marker (location query)
        let newLocation: string | [number, number] | undefined =
          locationSelectionsRef.current[source.collectionId];
        if (isDirectClickOnOwnedMarker && nearestOwnedFeature) {
          // Direct click on marker - use location query with marker ID
          newLocation = nearestOwnedFeature.id;
        } else if (supportsPosition && targetLocation) {
          if (radiusPending) {
            // Radius query in flight — defer until gridPointData arrives
            return;
          }
          // Snap to nearest grid point when available, otherwise use exact clicked coordinates
          newLocation = gridPoint ?? targetLocation;
        } else if (nearestOwnedFeature) {
          // Fall back to location query with nearest marker
          newLocation = nearestOwnedFeature.id;
        } else if (isLocalFreePositionClick) {
          newLocation = undefined;
        }

        const hasInitialLocation = Object.hasOwn(
          initialLocationsRef.current,
          source.collectionId,
        );
        const initialLocation =
          initialLocationsRef.current[source.collectionId];

        if (
          !isEqual(currentLocation, newLocation) &&
          (!hasInitialLocation || !isEqual(initialLocation, newLocation))
        ) {
          dispatch(
            soundingsActions.updateSelectedLocation({
              sourceId: source.collectionId,
              location: newLocation,
              viewId,
              origin: SoundingsActionOrigin.soundingsComponent,
            }),
          );
        }
      }
    });

    // After processing all sources, determine per-source highlights:
    // Each source that doesn't support position queries gets its nearest feature highlighted
    // with the same color as its sounding diagram
    const newHighlights: Record<string, string> = {};

    allNearestFeatures.forEach(({ feature, sourceId }) => {
      const supportsPosition = positionQuerySupportBySource[sourceId] ?? false;
      if (!supportsPosition && feature?.id) {
        // Only set nearby highlight when user clicked on map (not directly on a location marker).
        // Detect direct click by checking if the feature matches the selected feature.
        const isDirectClickOnStation =
          currentSelectedFeature?.id === feature.id &&
          currentMapPin?.id === currentSelectedFeature?.id;

        if (!isDirectClickOnStation) {
          newHighlights[sourceId] = feature.id;
        }
      }
    });

    updateHighlightedFeaturesBySource(newHighlights);

    // Reset the processing flag after a timeout to allow React's full update cycle to complete
    setTimeout(() => {
      isProcessingLocationUpdateRef.current = false;
    }, 0);
    // Note: selectedFeature and selectedMapPin are read from refs to avoid dependency cycles
  }, [
    dispatch,
    locationData,
    positionQuerySupportBySource,
    radiusQuerySupportBySource,
    gridPointData,
    targetLocation,
    sourceSelections,
    viewId,
    updateHighlightedFeaturesBySource,
  ]);

  // Effect 2 was removed - it was redundant with effect 1 which handles all cases
  // (with or without selectedFeature, falls back to targetLocation if no nearby feature)

  useEffect(() => {
    if (!locationData || locationData.length === 0) {
      return;
    }
    if (selectedMapIds.length === 0) {
      return;
    }

    // Prevent re-entrant updates from location sync effects
    if (isProcessingLocationUpdateRef.current) {
      return;
    }

    const changedSourceIds = sourceSelections.reduce<string[]>(
      (ids, source) => {
        if (!source?.collectionId) {
          return ids;
        }

        const currentLocation = locationSelections[source.collectionId];
        const previousLocation =
          previousLocationsRef.current[source.collectionId];

        if (currentLocation !== previousLocation) {
          return ids.concat(source.collectionId);
        }

        return ids;
      },
      [],
    );

    if (changedSourceIds.length === 0) {
      return;
    }

    isProcessingLocationUpdateRef.current = true;

    changedSourceIds.forEach((changedSourceId) => {
      const selectedLocation = locationSelections[changedSourceId];
      if (!selectedLocation) {
        return;
      }

      previousLocationsRef.current = {
        ...previousLocationsRef.current,
        [changedSourceId]: selectedLocation,
      };

      const serviceData = locationData.find(
        (service) => service.id === changedSourceId,
      );
      if (!serviceData) {
        return;
      }

      if (typeof selectedLocation === 'string') {
        const foundFeature = serviceData.features?.find(
          (feature) => feature.id === selectedLocation,
        );
        if (foundFeature?.geometry.type === 'Point') {
          const [lon, lat] = foundFeature.geometry.coordinates;
          // Use refs for synchronous check (state updates are async)
          // Check per-source highlight to correctly handle multiple sources with different highlights
          const sourceHighlight =
            highlightedFeaturesBySourceRef.current[changedSourceId];
          const isNearestFeatureFromClick =
            sourceHighlight === selectedLocation;
          const currentMapPin = selectedMapPinRef.current;
          const currentFeature = selectedFeatureRef.current;

          // When a string location is explicitly selected (from menu),
          // always clear the highlights to show red marker instead of colored highlights
          if (sourceHighlight) {
            updateHighlightedFeaturesBySource({});
          }

          // Skip map pin update only when this is the highlighted nearest feature from a map click
          // AND the pin is already at the correct location
          // Allow update when user selects a different location from menu
          const mapPinNeedsUpdate =
            !isNearestFeatureFromClick &&
            (currentMapPin?.id !== selectedLocation ||
              currentMapPin?.lon !== lon ||
              currentMapPin?.lat !== lat);
          // Skip selectedFeature update for nearby clicks to keep colored styling
          const featureNeedsUpdate =
            !isNearestFeatureFromClick &&
            currentFeature?.id !== selectedLocation;

          if (mapPinNeedsUpdate) {
            selectedMapIds.forEach((mapId) => {
              dispatch(
                mapActions.setMapPinLocation({
                  mapId,
                  mapPinLocation: { lon, lat, id: selectedLocation },
                }),
              );
            });
          }
          if (featureNeedsUpdate) {
            ownedFeatureLayerIds.forEach((layerId) => {
              dispatch(
                layerActions.setSelectedFeatures({
                  layerId,
                  selectedFeatureIds: [selectedLocation],
                }),
              );
            });
          }
        }
      } else if (Array.isArray(selectedLocation)) {
        const [lon, lat] = selectedLocation;
        const currentMapPin = selectedMapPinRef.current;

        // When locationSelections was snapped to a grid point (differs from the clicked
        // position), keep the map pin at the clicked position so targetLocation stays at the
        // click and the legend can compute a meaningful distance to the data origin.
        const isGridPointSnap =
          targetLocationRef.current !== undefined &&
          !isEqual(selectedLocation, targetLocationRef.current);

        if (!isGridPointSnap) {
          const needsUpdate =
            currentMapPin?.lon !== lon || currentMapPin?.lat !== lat;

          if (needsUpdate) {
            selectedMapIds.forEach((mapId) => {
              dispatch(
                mapActions.setMapPinLocation({
                  mapId,
                  mapPinLocation: { lon, lat },
                }),
              );
            });
          }
        }
      }
    });

    // Reset the processing flag after a timeout to allow React's full update cycle to complete
    setTimeout(() => {
      isProcessingLocationUpdateRef.current = false;
    }, 0);
  }, [
    locationData,
    locationSelections,
    sourceSelections,
    selectedMapIds,
    ownedFeatureLayerIds,
    dispatch,
    viewId,
    updateHighlightedFeaturesBySource,
    // Note: highlightedFeaturesBySource intentionally excluded - we use highlightedFeaturesBySourceRef.current
    // inside this effect, and including the state would cause infinite loops since we call
    // updateHighlightedFeaturesBySource within the effect
  ]);

  const { error, data, isPending } = useMultipleSources(
    resolvedSourceSelections,
    locationSelections,
    selectedTimeInUse,
  );

  useEffect(() => {
    setIsTimeMismatchDismissed(false);
  }, [mismatchKey]);

  const soundingPanelsInOrder = useSelector((store: SoundingsModuleState) =>
    getSoundingPanelsInOrder(store, viewId),
  );

  const changeOrder = (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ): void => {
    const orderedIds = newOrder.map((collection) => collection.id);
    const currentOrderedIds = soundingPanelsInOrder.map((panel) => panel?.id);
    if (isEqual(orderedIds, currentOrderedIds)) {
      return;
    }
    dispatch(
      soundingsActions.changeSoundingPanelOrder({
        viewId,
        newOrderOfIds: orderedIds,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  };

  return (
    <>
      {!!isPending && (
        <LinearProgress
          color="secondary"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 20,
          }}
        />
      )}
      <Stack
        ref={soundingsRef}
        sx={{
          padding: '12px',
          width: '100%',
          height: '100%',
          backgroundColor: 'geowebColors.background.surfaceApp',
          position: 'relative',
          containerType: 'inline-size',
          containerName: SOUNDINGS_CONTAINER_NAME,
        }}
      >
        <Box>
          <MenuBarConnect
            selectedLocationName={selectedLocationName}
            viewId={viewId}
          />
        </Box>
        <Card sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Stack
            spacing={1}
            sx={{ width: '100%', height: '100%', display: 'flex' }}
          >
            {!!error && (
              <Box sx={{ flex: '0 0 auto' }}>
                <AlertBanner shouldClose title={error.message} />
              </Box>
            )}
            {!!timeMismatchInfo && !isTimeMismatchDismissed && (
              <Box sx={{ flex: '0 0 auto', position: 'relative' }}>
                <AlertBanner
                  severity="warning"
                  title={t('soundings-time-mismatch-title')}
                />
                <IconButton
                  aria-label="Close warning"
                  onClick={(): void => setIsTimeMismatchDismissed(true)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 10,
                    padding: 0.25,
                    zIndex: 1,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
            {isLegendVisible ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: '2px',
                  paddingRight: '2px',
                  minHeight: '24px',
                  width: '100%',
                }}
              >
                <SoundingsLegend
                  selectedPoint={targetLocation}
                  sourceGridPoints={sourceGridPoints}
                  sourceLocationNames={sourceLocationNames}
                  sourceResolvedInstanceIds={sourceResolvedInstanceIds}
                  sourceSelections={sourceSelections}
                />
              </Box>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <SharedMouseYProvider>
                <SoundingsPanels
                  chosenDiagramType={diagramType}
                  data={data}
                  isMouseWheelZoomEnabled={!isTimeScrollingEnabled}
                  onOrderChange={changeOrder}
                  panelComponent={SoundingsPanelConnect}
                  profileDataPreferences={profileDataPreferences}
                  setChosenDiagramType={setChosenDiagramType}
                  soundingPanelsInOrder={soundingPanelsInOrder}
                  viewId={viewId}
                />
              </SharedMouseYProvider>
            </Box>
            <Box
              sx={{
                width: '100%',
                flex: '0 0 auto',
              }}
            >
              <SoundingsTimeSliderConnect
                soundingsWindowRef={soundingsRef}
                syncEnabled={isTimeSynced}
                timeInUse={selectedTimeInUse}
                viewId={viewId}
              />
            </Box>
          </Stack>
        </Card>
      </Stack>
    </>
  );
};
